import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import DataTable from '../../components/DataTable';
import DebouncedSearchInput from '../../components/DebouncedSearchInput';
import customerAPI from '../../api/customerAPI';

const CustomerListPage = () => {
  const navigate = useNavigate();

  const [allCustomers, setAllCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [customerTypeFilter, setCustomerTypeFilter] = useState('all');

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    filterCustomers();
  }, [searchTerm, customerTypeFilter, allCustomers]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await customerAPI.getAll({ limit: 9999 });
      setAllCustomers(res.data || []);
    } catch (err) {
      setError(err || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const filterCustomers = () => {
    let filtered = [...allCustomers];

    if (customerTypeFilter !== 'all') {
      filtered = filtered.filter(c => c.customerType === customerTypeFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        (c.name || '').toLowerCase().includes(term) ||
        (c.email || '').toLowerCase().includes(term) ||
        (c.phone || '').toLowerCase().includes(term)
      );
    }

    setFilteredCustomers(filtered);
  };

  const exportToCSV = () => {
    if (!allCustomers.length) return;

    const headers = [
      'Name', 'Type', 'Email', 'Phone', 'Loyalty Points', 'Address', 'City', 'Contact Person'
    ];

    const rows = allCustomers.map(c => [
      c.name || '',
      c.customerType?.replace('_', ' ') || '',
      c.email || '',
      c.phone || '',
      c.loyaltyPoints || 0,
      c.address || '',
      c.city || '',
      c.contactPerson || ''
    ]);

    let csvContent = '';
    csvContent += headers.join(',') + '\n';
    rows.forEach(row => {
      // Escape commas in fields
      const escapedRow = row.map(field => `"${(field ?? '').toString().replace(/"/g, '""')}"`);
      csvContent += escapedRow.join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'customers.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      title: 'Customer Name',
      key: 'name',
      render: (c) => <span className="font-medium">{c.name}</span>
    },
    {
      title: 'Type',
      key: 'customerType',
      render: (c) => (
        <span className="capitalize">
          {c.customerType?.replace('_', ' ') || 'POS'}
        </span>
      )
    },
    {
      title: 'Email',
      key: 'email',
      render: (c) => c.email || '—'
    },
    {
      title: 'Phone',
      key: 'phone',
      render: (c) => c.phone || '—'
    },
    {
      title: 'Loyalty Points',
      key: 'loyaltyPoints',
      render: (c) => c.loyaltyPoints || 0
    }
  ];

  return (
    <Layout pageTitle="Customers">
      <div className="bg-gray-100 mx-auto p-6">

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Header Controls */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-4">
            <DebouncedSearchInput
              placeholder="Search by name, email or phone..."
              onSearch={setSearchTerm}
              className="flex-1 mr-4 max-w-md"
            />

            <select
              value={customerTypeFilter}
              onChange={(e) => setCustomerTypeFilter(e.target.value)}
              className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">All Types</option>
              <option value="pos">POS</option>
              <option value="trucking">Trucking</option>
              <option value="freight_forwarding">Freight Forwarding</option>
            </select>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Export CSV
            </button>

            <button
              onClick={() => navigate('/customer/new')}
              className="transition-all duration-300 px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600 flex items-center"
            >
              <span className="mr-1">+</span> New Customer
            </button>
          </div>
        </div>

        {searchTerm && (
          <div className="text-sm text-gray-500 mb-2">
            Showing {filteredCustomers.length} of {allCustomers.length} customers
          </div>
        )}

        <DataTable
          columns={columns}
          data={filteredCustomers}
          loading={loading}
          emptyMessage="No customers found"
          onRowClick={(c) => navigate(`/customer/${c._id}`)}
        />
      </div>
    </Layout>
  );
};

export default CustomerListPage;
